import type {
  Equipment,
  EquipmentImageBinding,
  EquipmentPatchRequest,
  EquipmentWriteRequest,
} from '../api/active/generated/equipment';
import { localImagePreviewUrl } from '../catalog-media/localImagePreview';
import type {
  DeviceCatalogRecord,
  DeviceImageAsset,
} from '../pages/cpq-v2/state/types';

export const equipmentFieldNames = [
  'name',
  'sku',
  'jkyCode',
  'category',
  'deviceAttribute',
  'procurementAttribute',
  'procurementCycle',
  'weight',
  'sortOrder',
  'status',
  'remark',
  'localPoint',
  'localPrice',
  'offsitePoint',
  'offsitePrice',
  'rentalPrice',
  'packageItemPrice',
  'shortRentalPriceYuan',
  'taxInclusivePlanPrice',
  'unitPrice',
  'untaxedPlanPrice',
  'retailPriceTax13',
  'buyoutFinalPriceTax13',
  'purchaseFinalPriceExTax13',
  'dynamicCost',
  'compensationPrice',
  'images',
  'replacements',
  'customFields',
] as const;

export type EquipmentFieldName = (typeof equipmentFieldNames)[number];

const yuan = (cents: number | null | undefined) =>
  cents == null ? Number.NaN : cents / 100;
export const purchaseCents = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return null;
  if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(String(value)))
    throw new Error('购买最终价须为非负金额且最多两位小数');
  // 当前表单使用 number；保守限制在 2^45 元以下，避免输入阶段相邻分值
  // 合并为同一浮点数。超界明确拒绝，绝不把已经舍入的数值伪装成原始金额。
  if (value >= 2 ** 45)
    throw new Error(
      '购买最终价超出页面逐分精确处理范围，金额须小于35184372088832元',
    );
  // 从十进制文本拆整数和小数；禁止浮点乘100后四舍五入，0.29也必须精确为29分。
  const [whole, fraction = ''] = String(value).split('.');
  const result = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  if (result > BigInt(Number.MAX_SAFE_INTEGER))
    throw new Error('购买最终价超出精确金额范围');
  return Number(result);
};
const nullableYuan = (value: number | null | undefined): number | null =>
  value === null
    ? null
    : typeof value === 'number' && Number.isFinite(value)
      ? value / 100
      : Number.NaN;
const cents = (value: number | null | undefined) => purchaseCents(value);
const optionalRawText = (value: string | undefined) =>
  value === undefined || value.trim() === '' ? null : value;
const optionalCategory = (value: string) => {
  const normalized = value.trim();
  return normalized === '' ? null : normalized;
};

export function mapEquipment(equipment: Equipment): DeviceCatalogRecord {
  const images: DeviceImageAsset[] = (equipment.images ?? [])
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((image) => ({
      id: image.storedObjectId,
      storedObjectId: image.storedObjectId,
      source: localImagePreviewUrl(image.previewUrl),
      previewExpiresAt: image.previewExpiresAt,
      thumbnailSource: localImagePreviewUrl(
        image.thumbnailUrl ?? image.previewUrl,
      ),
      thumbnailExpiresAt: image.thumbnailExpiresAt ?? image.previewExpiresAt,
      name: image.storedObjectId,
      mimeType: image.mediaType,
      size: image.byteSize,
      order: image.sortOrder,
      kind: 'remote',
    }));
  return {
    imagePresence:
      equipment.images === undefined
        ? undefined
        : images.length
          ? 'present'
          : 'empty',
    customFields: equipment.customFields,
    customFieldPreviews: equipment.customFieldPreviews,
    id: equipment.id,
    equipmentCode: equipment.equipmentCode,
    currentRevisionId: equipment.currentRevisionId,
    resourceVersion: String(equipment.resourceVersion),
    name: equipment.name ?? '',
    sku: equipment.skuCode ?? null,
    jkyCode: equipment.jkyCode ?? '',
    category: equipment.category ?? '',
    deviceAttribute: equipment.deviceAttribute ?? undefined,
    procurementAttribute: equipment.procurementAttribute ?? undefined,
    procurementCycle: equipment.procurementCycle ?? undefined,
    weight: equipment.weightKg == null ? null : Number(equipment.weightKg),
    sortOrder: equipment.sortOrder ?? 0,
    status: equipment.status ?? 'inactive',
    remark: equipment.remark ?? '',
    localPoint: equipment.localPoint ?? '',
    localPrice: equipment.localPrice ?? '',
    offsitePoint: equipment.offsitePoint ?? '',
    offsitePrice: equipment.offsitePrice ?? '',
    rentalPrice: nullableYuan(equipment.rentalPrice),
    packageItemPrice: nullableYuan(equipment.packageItemPrice),
    shortRentalPriceYuan: nullableYuan(equipment.shortRentalPrice),
    formalShortRentalPriceCents: equipment.shortRentalPrice,
    taxInclusivePlanPrice: nullableYuan(equipment.taxInclusivePlanPrice),
    unitPrice: nullableYuan(equipment.unitPrice),
    untaxedPlanPrice: nullableYuan(equipment.untaxedPlanPrice),
    retailPriceTax13: nullableYuan(equipment.retailPriceTax13),
    buyoutFinalPriceTax13: yuan(equipment.buyoutFinalPriceTax13),
    purchaseFinalPriceExTax13: yuan(equipment.purchaseFinalPriceExTax13),
    formalPurchasePrices: {
      revisionId: equipment.currentRevisionId,
      tax13Cents: equipment.buyoutFinalPriceTax13 ?? null,
      exTax13Cents: equipment.purchaseFinalPriceExTax13 ?? null,
    },
    dynamicCost: nullableYuan(equipment.dynamicCost),
    compensationPrice: nullableYuan(equipment.compensationPrice),
    pricingFieldAvailability: {
      rentalPrice:
        typeof equipment.rentalPrice === 'number' &&
        Number.isFinite(equipment.rentalPrice),
      packageItemPrice:
        typeof equipment.packageItemPrice === 'number' &&
        Number.isFinite(equipment.packageItemPrice),
      shortRentalPriceYuan:
        typeof equipment.shortRentalPrice === 'number' &&
        Number.isFinite(equipment.shortRentalPrice),
      taxInclusivePlanPrice: equipment.taxInclusivePlanPrice != null,
      unitPrice: equipment.unitPrice != null,
      untaxedPlanPrice:
        typeof equipment.untaxedPlanPrice === 'number' &&
        Number.isFinite(equipment.untaxedPlanPrice),
      retailPriceTax13:
        typeof equipment.retailPriceTax13 === 'number' &&
        Number.isFinite(equipment.retailPriceTax13),
      buyoutFinalPriceTax13: equipment.buyoutFinalPriceTax13 != null,
      purchaseFinalPriceExTax13: equipment.purchaseFinalPriceExTax13 != null,
      dynamicCost:
        typeof equipment.dynamicCost === 'number' &&
        Number.isFinite(equipment.dynamicCost),
      compensationPrice:
        typeof equipment.compensationPrice === 'number' &&
        Number.isFinite(equipment.compensationPrice),
    },
    image:
      images.find(
        (image) =>
          image.id ===
          equipment.images?.find((entry) => entry.isPrimary)?.storedObjectId,
      )?.source ?? '',
    thumbnail:
      images.find(
        (image) =>
          image.id ===
          equipment.images?.find((entry) => entry.isPrimary)?.storedObjectId,
      )?.thumbnailSource ?? '',
    gallery: images.map((image) => image.source),
    thumbnailGallery: images.map(
      (image) => image.thumbnailSource ?? image.source,
    ),
    images,
    primaryImageId:
      equipment.images?.find((image) => image.isPrimary)?.storedObjectId ??
      null,
    replacementIds: (equipment.replacements ?? [])
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => item.replacementDeviceId),
    stockByWarehouse: {},
    updatedAt: equipment.updatedAt,
    updatedAtSource: 'user-save',
  };
}

export function viewableEquipmentFields(
  equipment: Equipment,
): Set<EquipmentFieldName> {
  const map: Record<EquipmentFieldName, keyof Equipment> = {
    name: 'name',
    sku: 'skuCode',
    jkyCode: 'jkyCode',
    category: 'category',
    deviceAttribute: 'deviceAttribute',
    procurementAttribute: 'procurementAttribute',
    procurementCycle: 'procurementCycle',
    weight: 'weightKg',
    sortOrder: 'sortOrder',
    status: 'status',
    remark: 'remark',
    localPoint: 'localPoint',
    localPrice: 'localPrice',
    offsitePoint: 'offsitePoint',
    offsitePrice: 'offsitePrice',
    rentalPrice: 'rentalPrice',
    packageItemPrice: 'packageItemPrice',
    shortRentalPriceYuan: 'shortRentalPrice',
    taxInclusivePlanPrice: 'taxInclusivePlanPrice',
    unitPrice: 'unitPrice',
    untaxedPlanPrice: 'untaxedPlanPrice',
    retailPriceTax13: 'retailPriceTax13',
    buyoutFinalPriceTax13: 'buyoutFinalPriceTax13',
    purchaseFinalPriceExTax13: 'purchaseFinalPriceExTax13',
    dynamicCost: 'dynamicCost',
    compensationPrice: 'compensationPrice',
    images: 'images',
    replacements: 'replacements',
    customFields: 'customFields',
  };
  return new Set(equipmentFieldNames.filter((name) => map[name] in equipment));
}

export function imageBindings(
  images: DeviceImageAsset[],
  primaryImageId: string | null,
): EquipmentImageBinding[] {
  return images.map((image, index) => {
    if (!image.storedObjectId)
      throw new Error('图片尚未完成服务端扫描，不能保存');
    return {
      storedObjectId: image.storedObjectId,
      mediaType: image.mimeType as EquipmentImageBinding['mediaType'],
      byteSize: image.size,
      isPrimary: image.id === primaryImageId,
      sortOrder: index,
    };
  });
}

export function equipmentWrite(
  record: DeviceCatalogRecord,
  revisions: ReadonlyMap<string, string>,
): EquipmentWriteRequest {
  return {
    customFields: record.customFields ?? [],
    name: record.name.trim(),
    sku: record.sku?.trim() || null,
    jkyCode: record.jkyCode.trim() || null,
    category: optionalCategory(record.category),
    deviceAttribute: record.deviceAttribute ?? null,
    procurementAttribute: record.procurementAttribute ?? null,
    procurementCycle: record.procurementCycle?.trim() || null,
    weightKg: record.weight == null ? null : String(record.weight),
    sortOrder: record.sortOrder,
    status: record.status,
    remark: record.remark || null,
    localPoint: optionalRawText(record.localPoint),
    localPrice: optionalRawText(record.localPrice),
    offsitePoint: optionalRawText(record.offsitePoint),
    offsitePrice: optionalRawText(record.offsitePrice),
    rentalPrice: cents(record.rentalPrice),
    packageItemPrice: cents(record.packageItemPrice),
    shortRentalPrice: cents(record.shortRentalPriceYuan),
    taxInclusivePlanPrice: purchaseCents(record.taxInclusivePlanPrice),
    untaxedPlanPrice: cents(record.untaxedPlanPrice),
    retailPriceTax13: cents(record.retailPriceTax13),
    dynamicCost: cents(record.dynamicCost),
    compensationPrice: cents(record.compensationPrice),
    images: imageBindings(record.images, record.primaryImageId),
    replacements: record.replacementIds.map(
      (replacementDeviceId, sortOrder) => {
        const replacementDeviceRevisionId = revisions.get(replacementDeviceId);
        if (!replacementDeviceRevisionId)
          throw new Error('平替设备缺少精确 revision，不能保存');
        return { replacementDeviceId, replacementDeviceRevisionId, sortOrder };
      },
    ),
  };
}

const same = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

export function equipmentPatch(
  record: DeviceCatalogRecord,
  original: Equipment,
  revisions: ReadonlyMap<string, string>,
): EquipmentPatchRequest {
  const visible = viewableEquipmentFields(original);
  const patch: EquipmentPatchRequest = {};
  if (
    Object.hasOwn(original, 'customFields') &&
    !same(record.customFields ?? [], original.customFields ?? [])
  )
    patch.customFields = record.customFields ?? [];
  const include = <K extends keyof EquipmentPatchRequest>(
    field: EquipmentFieldName,
    key: K,
    next: EquipmentPatchRequest[K],
    previous: unknown,
  ) => {
    if (visible.has(field) && !same(next, previous)) patch[key] = next;
  };

  include('name', 'name', record.name.trim(), original.name);
  include('sku', 'sku', record.sku?.trim() || null, original.skuCode ?? null);
  include(
    'jkyCode',
    'jkyCode',
    record.jkyCode.trim() || null,
    original.jkyCode,
  );
  include(
    'category',
    'category',
    optionalCategory(record.category),
    original.category ?? null,
  );
  include(
    'deviceAttribute',
    'deviceAttribute',
    record.deviceAttribute ?? null,
    original.deviceAttribute,
  );
  include(
    'procurementAttribute',
    'procurementAttribute',
    record.procurementAttribute ?? null,
    original.procurementAttribute,
  );
  include(
    'procurementCycle',
    'procurementCycle',
    record.procurementCycle?.trim() || null,
    original.procurementCycle?.trim() || null,
  );
  include(
    'weight',
    'weightKg',
    record.weight == null ? null : String(record.weight),
    original.weightKg == null ? null : String(Number(original.weightKg)),
  );
  include('sortOrder', 'sortOrder', record.sortOrder, original.sortOrder);
  include('status', 'status', record.status, original.status);
  include('remark', 'remark', record.remark || null, original.remark);
  include(
    'localPoint',
    'localPoint',
    optionalRawText(record.localPoint),
    original.localPoint,
  );
  include(
    'localPrice',
    'localPrice',
    optionalRawText(record.localPrice),
    original.localPrice,
  );
  include(
    'offsitePoint',
    'offsitePoint',
    optionalRawText(record.offsitePoint),
    original.offsitePoint,
  );
  include(
    'offsitePrice',
    'offsitePrice',
    optionalRawText(record.offsitePrice),
    original.offsitePrice,
  );
  include(
    'rentalPrice',
    'rentalPrice',
    cents(record.rentalPrice),
    original.rentalPrice,
  );
  include(
    'packageItemPrice',
    'packageItemPrice',
    cents(record.packageItemPrice),
    original.packageItemPrice,
  );
  include(
    'shortRentalPriceYuan',
    'shortRentalPrice',
    cents(record.shortRentalPriceYuan),
    original.shortRentalPrice,
  );
  include(
    'taxInclusivePlanPrice',
    'taxInclusivePlanPrice',
    purchaseCents(record.taxInclusivePlanPrice),
    original.taxInclusivePlanPrice,
  );
  include(
    'untaxedPlanPrice',
    'untaxedPlanPrice',
    cents(record.untaxedPlanPrice),
    original.untaxedPlanPrice,
  );
  include(
    'retailPriceTax13',
    'retailPriceTax13',
    cents(record.retailPriceTax13),
    original.retailPriceTax13,
  );
  include(
    'dynamicCost',
    'dynamicCost',
    cents(record.dynamicCost),
    original.dynamicCost,
  );
  include(
    'compensationPrice',
    'compensationPrice',
    cents(record.compensationPrice),
    original.compensationPrice,
  );

  if (visible.has('images')) {
    const next = imageBindings(record.images, record.primaryImageId);
    const previous = (original.images ?? []).map((image) => ({
      storedObjectId: image.storedObjectId,
      mediaType: image.mediaType,
      byteSize: image.byteSize,
      isPrimary: image.isPrimary,
      sortOrder: image.sortOrder,
    }));
    if (!same(next, previous)) patch.images = next;
  }
  if (visible.has('replacements')) {
    const next = record.replacementIds.map((replacementDeviceId, sortOrder) => {
      const replacementDeviceRevisionId = revisions.get(replacementDeviceId);
      if (!replacementDeviceRevisionId)
        throw new Error('平替设备缺少精确 revision，不能保存');
      return { replacementDeviceId, replacementDeviceRevisionId, sortOrder };
    });
    const previous = (original.replacements ?? []).map((replacement) => ({
      replacementDeviceId: replacement.replacementDeviceId,
      replacementDeviceRevisionId: replacement.replacementDeviceRevisionId,
      sortOrder: replacement.sortOrder,
    }));
    if (!same(next, previous)) patch.replacements = next;
  }
  return patch;
}
